import { Fragment, useMemo, type ReactNode } from "react";
import { MarkdownReact } from "@/components/MarkdownReact";
import { formatWeight } from "@/lib/utils";
import {
  formatQuoteAmount,
  formatQuoteLineTotal,
  formatQuoteUnitPrice,
  getQuoteDisplayTotals,
} from "@/lib/quoteCurrency";
import Decimal from "decimal.js";
import {
  WEIGHT_UNIT_ABBREVIATION,
  formatDateOnly,
  type PublicQuote,
  type PublicQuoteItemLine,
} from "@forgepoint/shared";
import {
  applyEscalator,
  asString,
  calculateMlf,
  getSteelDistributorEscalators,
  type ActiveEscalator,
} from "./steelDistributorEscalators";
import { CoverLetterBlock } from "../components/CoverLetterBlock";
import {
  computeLineNumbers,
  formatAddressLines,
  getQuoteEffectiveDate,
  getBreakWeightUnit,
  getTotalWeightsByUnit,
} from "../utils";
import {
  PdfPageViewer,
  usePdfPageInfo,
  type QuoteSection,
} from "../components/PdfPageViewer";

interface PublicQuoteTemplateSteelDistributorProps {
  quote: PublicQuote;
  isExpired: boolean;
}

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
};

const formatDate = (value?: Date | string | null) => {
  if (!value) {
    return null;
  }
  if (typeof value === "string") {
    return formatDateOnly(value, DATE_FORMAT_OPTIONS, "en-US");
  }
  if (Number.isNaN(value.getTime())) {
    return null;
  }
  return value.toLocaleDateString("en-US", DATE_FORMAT_OPTIONS);
};

const LABEL_CELL_CLASS =
  "border border-gray-400 px-2 py-1 font-bold text-gray-900 text-xs";

const LINE_TABLE_CLASS = "w-full border-collapse text-xs table-fixed";

/** Enforces consistent column widths across separate item tables. */
const LINE_TABLE_COLGROUP = (
  <colgroup>
    <col style={{ width: "5%" }} />
    <col style={{ width: "37%" }} />
    <col style={{ width: "15%" }} />
    <col style={{ width: "14%" }} />
    <col style={{ width: "14%" }} />
    <col style={{ width: "15%" }} />
  </colgroup>
);

/** Renders an address as stacked lines of text. */
function renderAddressBlock(addressLines: string[] | null) {
  if (!addressLines) {
    return null;
  }
  return (
    <div className="text-[10px] text-gray-900 uppercase">
      {addressLines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

/**
 * Steel Distributor public quote template.
 * Renders content as paginated US Letter-sized pages via PdfPageViewer.
 * @param quote - The hydrated quote data
 * @param isExpired - Whether the quote has expired
 */
export function PublicQuoteTemplateSteelDistributor({
  quote,
  isExpired,
}: PublicQuoteTemplateSteelDistributorProps) {
  const { sections, repeatableHeader } = useSteelDistributorSections(
    quote,
    isExpired,
  );
  return (
    <PdfPageViewer
      sections={sections}
      repeatableHeader={repeatableHeader}
      watermark={quote.watermark}
    />
  );
}

interface SteelDistributorLayout {
  sections: QuoteSection[];
  repeatableHeader: ReactNode;
}

/**
 * Builds an ordered array of pageable sections and a repeatable header from the quote data.
 * @param quote - The hydrated quote data
 * @param isExpired - Whether the quote has expired
 * @returns Sections and a repeatable header for PdfPageViewer
 */
function useSteelDistributorSections(
  quote: PublicQuote,
  isExpired: boolean,
): SteelDistributorLayout {
  const effectiveDate = formatDate(getQuoteEffectiveDate(quote));
  const expirationDate = formatDate(quote.expirationDate);
  const customerName = quote.customerCompany?.name ?? null;
  const customerAddressLines = formatAddressLines(
    quote.customerCompany?.address,
  );
  const shippingAddressLines = formatAddressLines(quote.shippingAddress);
  const orgAddressLines = formatAddressLines(quote.organization?.address);
  const lines = quote.lines ?? [];
  const customFields = quote.customFields ?? {};
  const displayTotals = getQuoteDisplayTotals(quote);

  const lineSequenceNumbers = useMemo(() => computeLineNumbers(lines), [lines]);

  const totalWeightsByUnit = useMemo(
    () => getTotalWeightsByUnit(lines),
    [lines],
  );

  const activeEscalators = useMemo(
    () => getSteelDistributorEscalators(quote),
    [quote],
  );

  // --- Repeatable header: org info, quote details, addresses, project info, and table header (shown on every page) ---
  const repeatableHeader = (
    <div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-4">
        <div className="space-y-0.5">
          {quote.organization?.logoUrl && (
            <img
              src={quote.organization.logoUrl}
              alt={`${quote.organization.name} logo`}
              className="h-10 w-auto object-contain mb-1"
            />
          )}
          <p className="text-base font-bold text-gray-900">
            {quote.organization?.name}
          </p>
          {renderAddressBlock(orgAddressLines)}
          {quote.organization?.phone && (
            <p className="text-[10px] text-gray-700">
              Telephone: {quote.organization.phone}
            </p>
          )}
          {quote.organization?.fax && (
            <p className="text-[10px] text-gray-700">
              Fax: {quote.organization.fax}
            </p>
          )}
        </div>

        <QuoteDetailsTable
          quoteDisplayNumber={quote.quoteDisplayNumber}
          customerName={customerName}
          effectiveDate={effectiveDate}
          expirationDate={expirationDate}
          isExpired={isExpired}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-600 mb-0.5">
            Quote For:
          </p>
          {customerName && (
            <p className="text-xs font-semibold text-gray-900 uppercase">
              {customerName}
            </p>
          )}
          {renderAddressBlock(customerAddressLines)}
        </div>
        {quote.shippingAddress && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-600 mb-0.5">
              Ship To:
            </p>
            {quote.shippingAddress.name && (
              <p className="text-xs font-semibold text-gray-900 uppercase">
                {quote.shippingAddress.name}
              </p>
            )}
            {renderAddressBlock(shippingAddressLines)}
          </div>
        )}
      </div>

      <ProjectInfoRow
        contractor={asString(customFields.Contractor)}
        projectName={
          asString(customFields["Project Name"]) ??
          quote.name ??
          quote.opportunity?.name ??
          null
        }
        contact={asString(customFields.Contact) ?? customerName}
        salesRepName={quote.salesRepName ?? null}
        salesRepEmail={quote.salesRepEmail ?? null}
      />

      {quote.deliveryTerms && (
        <div className="my-3 border border-gray-400 px-2 py-1 text-center">
          <p className="text-[10px] font-bold uppercase text-gray-900">
            {quote.deliveryTerms}
          </p>
        </div>
      )}

      <table className={LINE_TABLE_CLASS}>
        {LINE_TABLE_COLGROUP}
        <thead>
          <tr className="border-2 border-gray-900">
            <th className="px-1 py-1 text-center text-[10px] font-bold text-gray-900 uppercase">
              #
            </th>
            <th className="px-1.5 py-1 text-center text-[10px] font-bold text-gray-900 uppercase">
              Item Number / Description
            </th>
            <th className="px-1.5 py-1 text-center text-[10px] font-bold text-gray-900 uppercase">
              Quantity / L.F.
            </th>
            <th className="px-1.5 py-1 text-center text-[10px] font-bold text-gray-900 uppercase">
              Unit Price
            </th>
            <th className="px-1.5 py-1 text-center text-[10px] font-bold text-gray-900 uppercase">
              Weight
            </th>
            <th className="px-1.5 py-1 text-center text-[10px] font-bold text-gray-900 uppercase">
              Extended Amount
            </th>
          </tr>
        </thead>
      </table>
    </div>
  );

  const sections: QuoteSection[] = [];

  // --- Cover letter (full page, no repeatable header) ---
  if (quote.enableCoverLetter && quote.coverLetterMarkdown) {
    sections.push({
      key: "cover-letter",
      forcePageBreak: false,
      skipHeader: true,
      content: (
        <CoverLetterBlock
          coverLetterMarkdown={quote.coverLetterMarkdown}
          enableCoverLetter={quote.enableCoverLetter}
        />
      ),
    });
  }

  // --- Individual line items, each wrapped in its own table ---
  const hasCoverLetter =
    !!quote.enableCoverLetter && !!quote.coverLetterMarkdown;
  let firstLineAfterCoverLetter = hasCoverLetter;

  lines.forEach((line, index) => {
    const rendered = renderLine(
      line,
      index,
      quote.showTotals,
      activeEscalators,
      lines,
      lineSequenceNumbers[index],
      quote,
    );
    if (!rendered) {
      return;
    }

    const forceBreak = firstLineAfterCoverLetter;
    if (firstLineAfterCoverLetter) {
      firstLineAfterCoverLetter = false;
    }

    sections.push({
      key: `line-${index}`,
      forcePageBreak: forceBreak,
      content: (
        <table className={LINE_TABLE_CLASS}>
          {LINE_TABLE_COLGROUP}
          {rendered}
        </table>
      ),
    });
  });

  // --- Summary: totals + escalator pricing ---
  const hasSummary =
    (quote.showTotals &&
      (totalWeightsByUnit.length > 0 || quote.total !== null)) ||
    activeEscalators.length > 0;

  if (hasSummary) {
    sections.push({
      key: "summary",
      content: (
        <div>
          {quote.showTotals && (
            <table className="ml-auto border-collapse text-xs mt-3">
              <tbody>
                {totalWeightsByUnit.length > 0 &&
                  totalWeightsByUnit.map(([unit, weight]) => (
                    <tr key={unit}>
                      <td className={LABEL_CELL_CLASS}>
                        Total Weight (
                        {
                          WEIGHT_UNIT_ABBREVIATION[
                            unit as keyof typeof WEIGHT_UNIT_ABBREVIATION
                          ]
                        }
                        )
                      </td>
                      <td className="border border-gray-400 px-2 py-1 text-right text-gray-900">
                        {formatWeight(weight.toFixed(2))}
                      </td>
                    </tr>
                  ))}
                {quote.total && (
                  <tr>
                    <td className={`${LABEL_CELL_CLASS} text-sm`}>
                      Quote Total
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-right font-bold text-gray-900 text-sm">
                      {displayTotals.totalFormatted}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
          {activeEscalators.length > 0 && (
            <EscalatorSummaryTable
              activeEscalators={activeEscalators}
              quote={quote}
            />
          )}
        </div>
      ),
    });
  }

  // --- Quote footer ---
  if (quote.quoteFooter) {
    sections.push({
      key: "footer",
      content: (
        <div className="mt-3 pt-2 text-[10px] text-gray-800 whitespace-pre-wrap">
          {quote.quoteFooter}
        </div>
      ),
    });
  }

  // --- Signature (for already-signed quotes) ---
  if (quote.isSigned && quote.signature) {
    sections.push({
      key: "signature",
      skipHeader: true,
      content: (
        <div className="mt-3 border-t border-gray-400 pt-3">
          <table className="border-collapse text-xs">
            <tbody>
              <tr>
                <td className={LABEL_CELL_CLASS}>Authorized Signature</td>
                <td className="border border-gray-400 px-2 py-1">
                  <span className="font-signature text-xl text-gray-900">
                    {quote.signature.name}
                  </span>
                </td>
              </tr>
              <tr>
                <td className={LABEL_CELL_CLASS}>Date Signed</td>
                <td className="border border-gray-400 px-2 py-1 text-gray-900">
                  {new Date(quote.signature.signedAt).toLocaleDateString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ),
    });
  }

  // --- Terms of Service (always starts on a new page) ---
  if (quote.termsOfService) {
    sections.push({
      key: "terms",
      forcePageBreak: true,
      skipHeader: true,
      content: (
        <div>
          <h2 className="text-sm font-bold uppercase text-gray-900 mb-3 text-center border-b border-gray-300 pb-2">
            Terms and Conditions
          </h2>
          <div className="prose prose-xs prose-slate max-w-none text-gray-800 text-xs">
            <MarkdownReact>{quote.termsOfService}</MarkdownReact>
          </div>
        </div>
      ),
    });
  }

  return { sections, repeatableHeader };
}

/** Quote details mini-table in the header. */
function QuoteDetailsTable({
  quoteDisplayNumber,
  customerName,
  effectiveDate,
  expirationDate,
  isExpired,
}: {
  quoteDisplayNumber: string;
  customerName: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  isExpired: boolean;
}) {
  const { pageNumber, totalPages } = usePdfPageInfo();

  return (
    <table className="min-w-[200px] border-collapse text-xs">
      <tbody>
        <tr>
          <td className={LABEL_CELL_CLASS}>Quote #</td>
          <td className="border border-gray-400 px-1.5 py-0.5 text-gray-900">
            {quoteDisplayNumber}
          </td>
        </tr>
        {customerName && (
          <tr>
            <td className={LABEL_CELL_CLASS}>Customer</td>
            <td className="border border-gray-400 px-1.5 py-0.5 text-gray-900">
              {customerName}
            </td>
          </tr>
        )}
        {effectiveDate && (
          <tr>
            <td className={LABEL_CELL_CLASS}>Effective Date</td>
            <td className="border border-gray-400 px-1.5 py-0.5 text-gray-900">
              {effectiveDate}
            </td>
          </tr>
        )}
        {expirationDate && (
          <tr>
            <td className={LABEL_CELL_CLASS}>Expiration Date</td>
            <td
              className={`border border-gray-400 px-1.5 py-0.5 ${isExpired ? "text-red-600 font-semibold" : "text-gray-900"}`}
            >
              {expirationDate}
              {isExpired && <span className="block text-[8px]">Expired</span>}
            </td>
          </tr>
        )}
        {totalPages > 0 && (
          <tr>
            <td className={LABEL_CELL_CLASS}>Page</td>
            <td className="border border-gray-400 px-1.5 py-0.5 text-gray-900">
              {pageNumber} of {totalPages}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/** Project info 4-column row. */
function ProjectInfoRow({
  contractor,
  projectName,
  contact,
  salesRepName,
  salesRepEmail,
}: {
  contractor: string | null;
  projectName: string | null;
  contact: string | null;
  salesRepName: string | null;
  salesRepEmail: string | null;
}) {
  return (
    <table className="w-full border-collapse text-[10px] mb-3">
      <thead>
        <tr>
          <th className="border border-gray-400 px-1.5 py-0.5 text-center text-[10px] font-bold uppercase text-gray-900 w-1/4">
            Contractor
          </th>
          <th className="border border-gray-400 px-1.5 py-0.5 text-center text-[10px] font-bold uppercase text-gray-900 w-1/4">
            Project Name
          </th>
          <th className="border border-gray-400 px-1.5 py-0.5 text-center text-[10px] font-bold uppercase text-gray-900 w-1/4">
            Contact
          </th>
          <th className="border border-gray-400 px-1.5 py-0.5 text-center text-[10px] font-bold uppercase text-gray-900 w-1/4">
            Salesperson
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="border border-gray-300 px-1.5 py-0.5 text-center text-gray-900 uppercase">
            {contractor ?? <>&nbsp;</>}
          </td>
          <td className="border border-gray-300 px-1.5 py-0.5 text-center text-gray-900 uppercase">
            {projectName ?? <>&nbsp;</>}
          </td>
          <td className="border border-gray-300 px-1.5 py-0.5 text-center text-gray-900 uppercase">
            {contact ?? <>&nbsp;</>}
          </td>
          <td className="border border-gray-300 px-1.5 py-0.5 text-center text-gray-900 uppercase">
            {salesRepName ? (
              salesRepEmail ? (
                <a
                  href={`mailto:${salesRepEmail}`}
                  className="text-blue-700 underline"
                >
                  {salesRepName}
                </a>
              ) : (
                salesRepName
              )
            ) : (
              <>&nbsp;</>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/** Escalator pricing summary — separate table with dates and totals side by side. */
function EscalatorSummaryTable({
  activeEscalators,
  quote,
}: {
  activeEscalators: ReadonlyArray<ActiveEscalator>;
  quote: PublicQuote;
}) {
  return (
    <table className="ml-auto border-collapse text-xs mt-3">
      <thead>
        <tr>
          <th className="border border-gray-400 px-2 py-1 text-left text-[10px] font-bold text-gray-900 uppercase">
            Escalator Pricing
          </th>
          <th className="border border-gray-400 px-2 py-1 text-right text-[10px] font-bold text-gray-900 uppercase">
            %
          </th>
          <th className="border border-gray-400 px-2 py-1 text-right text-[10px] font-bold text-gray-900 uppercase">
            Total
          </th>
        </tr>
      </thead>
      <tbody>
        {activeEscalators.map((esc, i) => (
          <tr key={`esc-row-${i}`}>
            <td className="border border-gray-400 px-2 py-1 text-gray-900">
              {esc.dateLabel}
            </td>
            <td className="border border-gray-400 px-2 py-1 text-right text-gray-900">
              {esc.pct}%
            </td>
            <td className="border border-gray-400 px-2 py-1 text-right font-semibold text-gray-900">
              {formatQuoteAmount(esc.total.toFixed(2), quote)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Checks if a line is the first item after another item (for separator logic). */
function isNewItemGroup(
  lines: NonNullable<PublicQuote["lines"]>,
  index: number,
): boolean {
  if (index === 0) {
    return false;
  }
  const current = lines[index];
  if (current.type !== "ITEM") {
    return false;
  }
  for (let i = index - 1; i >= 0; i--) {
    if (lines[i].type === "ITEM") {
      return true;
    }
    if (lines[i].type === "SECTION") {
      return false;
    }
  }
  return false;
}

/**
 * Renders a single quote line as a table tbody element.
 * @param line - The quote line to render
 * @param index - The index of the line in the array
 * @param showTotals - Whether to display totals
 * @param activeEscalators - Active escalator pricing entries
 * @param allLines - All quote lines (for separator logic)
 * @param lineNumber - The display line number (null for non-numbered lines)
 * @param quote - The hydrated quote (for currency formatting)
 * @returns A tbody element or null
 */
function renderLine(
  line: NonNullable<PublicQuote["lines"]>[number],
  index: number,
  showTotals: boolean,
  activeEscalators: ReadonlyArray<ActiveEscalator>,
  allLines: NonNullable<PublicQuote["lines"]>,
  lineNumber: number | null,
  quote: PublicQuote,
) {
  if (line.type === "SECTION") {
    if (!line.description) {
      return null;
    }
    return (
      <tbody key={`section-${index}`}>
        <tr>
          <td
            colSpan={6}
            className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-800 border-t border-b border-gray-400"
          >
            {line.description}
          </td>
        </tr>
      </tbody>
    );
  }

  if (line.type === "NOTE") {
    if (!line.description) {
      return null;
    }
    return (
      <tbody key={`note-${index}`}>
        <tr>
          <td colSpan={6} className="px-1.5 py-0.5 text-xs text-gray-800">
            <MarkdownReact>{line.description}</MarkdownReact>
          </td>
        </tr>
      </tbody>
    );
  }

  if (line.type === "DISCOUNT") {
    if (!showTotals) {
      return null;
    }
    const discountFormatted =
      line.amount !== null ? formatQuoteLineTotal(line, quote) : null;
    return (
      <tbody key={`discount-${index}`}>
        <tr className="border-t border-gray-300">
          <td colSpan={5} className="px-1.5 py-0.5 text-xs text-gray-900">
            {line.description || "Discount"}
          </td>
          <td className="px-1.5 py-0.5 text-right text-xs font-semibold text-gray-900">
            {discountFormatted ? `-${discountFormatted}` : ""}
          </td>
        </tr>
      </tbody>
    );
  }

  if (line.type === "ITEM") {
    return (
      <ItemLineRows
        key={`item-${index}`}
        line={line}
        lineNumber={lineNumber}
        activeEscalators={activeEscalators}
        isNewGroup={isNewItemGroup(allLines, index)}
        quote={quote}
      />
    );
  }

  return null;
}

/** Renders an ITEM line: item name + qty/price/weight/amount, description below (no separator between them). */
function ItemLineRows({
  line,
  lineNumber,
  activeEscalators,
  isNewGroup,
  quote,
}: {
  line: PublicQuoteItemLine;
  lineNumber: number | null;
  activeEscalators: ReadonlyArray<ActiveEscalator>;
  isNewGroup: boolean;
  quote: PublicQuote;
}) {
  const itemName = line.item?.name ?? line.description ?? "Item";
  const itemDescription = formatItemDescription(line);
  const separatorClass = isNewGroup ? "border-t border-gray-300" : "";

  return (
    <tbody className="break-inside-avoid">
      {line.quantityBreaks.map((qb, qbIndex) => {
        const quantity = qb.quantity?.trim();
        if (!quantity) {
          return null;
        }

        const unitPrice = qb.unitPriceOverride ?? qb.unitPrice;
        const mlf = calculateMlf(
          unitPrice,
          line.item?.length,
          line.item?.dimensionUnit,
        );

        const weightVal = qb.totalWeight;
        const weightUnit = getBreakWeightUnit(qb, line);
        const weightDisplay =
          weightVal && weightUnit
            ? `${formatWeight(weightVal)} ${WEIGHT_UNIT_ABBREVIATION[weightUnit as keyof typeof WEIGHT_UNIT_ABBREVIATION] ?? ""}`
            : weightVal
              ? formatWeight(weightVal)
              : "";

        return (
          <Fragment key={`qb-${qbIndex}`}>
            {/* Main item row (first break) */}
            {qbIndex === 0 && (
              <tr className={separatorClass}>
                <td className="px-1 py-0.5 text-center text-xs text-gray-500 align-top">
                  {lineNumber}
                </td>
                <td className="px-1.5 py-0.5 text-xs font-semibold text-gray-900 align-top">
                  {itemName}
                </td>
                <td className="px-1.5 py-0.5 text-right text-xs text-gray-900 align-top">
                  {Number.isNaN(Number(quantity))
                    ? quantity
                    : new Intl.NumberFormat("en-US").format(Number(quantity))}
                </td>
                <td className="px-1.5 py-0.5 text-right text-gray-900 align-top">
                  {mlf ? (
                    <>
                      <span className="text-xs">{formatMlf(mlf)} MLF</span>
                      {unitPrice && (
                        <span className="block text-[8px] leading-none text-gray-500">
                          {formatQuoteUnitPrice(qb, quote)}
                        </span>
                      )}
                    </>
                  ) : unitPrice ? (
                    <span className="text-xs">
                      {formatQuoteUnitPrice(qb, quote)}
                    </span>
                  ) : (
                    ""
                  )}
                </td>
                <td className="px-1.5 py-0.5 text-right text-xs text-gray-900 align-top">
                  {weightDisplay}
                </td>
                <td className="px-1.5 py-0.5 text-right text-xs font-semibold text-gray-900 align-top">
                  {qb.amount ? formatQuoteLineTotal(qb, quote) : ""}
                </td>
              </tr>
            )}

            {/* Description row — no separator from item name */}
            {qbIndex === 0 && itemDescription && (
              <tr>
                <td />
                <td
                  colSpan={5}
                  className="whitespace-pre-line px-1.5 pb-px text-[10px] text-gray-600 italic"
                >
                  {itemDescription}
                </td>
              </tr>
            )}

            {/* Additional quantity breaks */}
            {qbIndex > 0 && (
              <tr>
                <td />
                <td className="px-1.5 py-0.5 text-xs text-gray-900 align-top" />
                <td className="px-1.5 py-0.5 text-right text-xs text-gray-900 align-top">
                  {Number.isNaN(Number(quantity))
                    ? quantity
                    : new Intl.NumberFormat("en-US").format(Number(quantity))}
                </td>
                <td className="px-1.5 py-0.5 text-right text-gray-900 align-top">
                  {mlf ? (
                    <>
                      <span className="text-xs">{formatMlf(mlf)} MLF</span>
                      {unitPrice && (
                        <span className="block text-[8px] leading-none text-gray-500">
                          {formatQuoteUnitPrice(qb, quote)}
                        </span>
                      )}
                    </>
                  ) : unitPrice ? (
                    <span className="text-xs">
                      {formatQuoteUnitPrice(qb, quote)}
                    </span>
                  ) : (
                    ""
                  )}
                </td>
                <td className="px-1.5 py-0.5 text-right text-xs text-gray-900 align-top">
                  {weightDisplay}
                </td>
                <td className="px-1.5 py-0.5 text-right text-xs font-semibold text-gray-900 align-top">
                  {qb.amount ? formatQuoteLineTotal(qb, quote) : ""}
                </td>
              </tr>
            )}
          </Fragment>
        );
      })}

      {/* Inline escalator sub-row */}
      {activeEscalators.length > 0 &&
        (() => {
          const firstQb = line.quantityBreaks[0];
          if (!firstQb) {
            return null;
          }
          const unitPrice = firstQb.unitPriceOverride ?? firstQb.unitPrice;
          const mlf = calculateMlf(
            unitPrice,
            line.item?.length,
            line.item?.dimensionUnit,
          );
          const escalatorBase =
            mlf ?? (unitPrice ? new Decimal(unitPrice) : null);
          if (!escalatorBase) {
            return null;
          }

          // Calculate cumulative percentages for each escalator
          let cumulativePct = 0;
          const escalatorPrices = activeEscalators.map((esc) => {
            cumulativePct += esc.pct;
            return applyEscalator(escalatorBase, cumulativePct);
          });

          return (
            <tr>
              <td colSpan={6} className="px-2 py-0.5 text-[10px] text-gray-700">
                <div className="flex flex-wrap justify-end gap-x-3 gap-y-1">
                  <span className="text-gray-500 uppercase">
                    Escalator Pricing
                  </span>
                  {escalatorPrices.map((price, i) => (
                    <span key={`esc-${i}`} className="whitespace-nowrap">
                      {activeEscalators[i].ordinalLabel}:{" "}
                      {mlf
                        ? formatMlf(price)
                        : formatQuoteAmount(price.toNumber(), quote)}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          );
        })()}
    </tbody>
  );
}

function formatMlf(value: Decimal): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value.floor().toNumber());
}

/** Builds a description string from line description and item description. */
function formatItemDescription(line: PublicQuoteItemLine): string | null {
  const details: string[] = [];
  const itemName = line.item?.name ?? null;

  if (line.description && line.description !== itemName) {
    details.push(line.description);
  }

  if (details.length === 0) {
    return null;
  }

  return details.join(" | ");
}
